import { data as routerData } from 'react-router';
import type { LoaderFunctionArgs } from '~/types/route-args';
import NetworkPage from '@modules/professional/page/NetworkPage';
import { loader as networkLoader } from '@modules/professional/page/loaders/network-page-loader.server';
import { isProfessionalRevampActive } from '@modules/professional/util/is-professional-revamp-active.server';
import { getSession } from '~/modules/root/util/session.server';
import { ProfessionalNetworkPageRevamp } from '~/ui-rocco/pages/professional-detail/ProfessionalNetworkPageRevamp';
import { shouldRevalidateLeaf } from '~/modules/root/util/should-revalidate-leaf';
import type { APHandle } from '~/modules/root';
import { inheritMeta } from '~/modules/seo';
import { useShowProfessionalRevamp } from '@modules/professional/hook/use-professional-layout-data';

export { inheritMeta as meta };

/**
 * When the revamp is active the Network tab becomes a full editorial page that
 * reuses the overview sections + a filterable partner-tile grid, so the loader
 * merges overview-shaped data (hero, "Talk to us", closing) with the network ES
 * data (tiles + pagination + filters). Otherwise it serves the legacy paginated
 * network grid.
 */
export const loader = async (args: LoaderFunctionArgs) => {
    if (!isProfessionalRevampActive(args.context, args.request)) {
        return networkLoader(args);
    }

    // The revamp's shared overview payload comes from the persistent layout
    // loader; this route only fetches the network tab's own data.
    const networkData = await networkLoader(args);
    const session = await getSession(args.request);

    return routerData(
        {
            networkProfessionals: networkData.networkProfessionals,
            networkHasMore: networkData.networkHasMore,
            pagination: networkData.pagination,
            filterStates: networkData.filterStates,
            filterStatesRocco: networkData.filterStatesRocco,
            aggregateScopeFilters: networkData.aggregateScopeFilters,
        },
        { headers: await session.getHeaders() },
    );
};

export const handle: APHandle = {
    category: 'professional',
    allowFilters: true,
    hideContactArchiPro: true,
    professionalRevampLayout: true,
};

const ProfessionalNetworkRoute = () => {
    const showRevamp = useShowProfessionalRevamp();

    return showRevamp ? <ProfessionalNetworkPageRevamp /> : <NetworkPage />;
};

export const shouldRevalidate = shouldRevalidateLeaf;
export default ProfessionalNetworkRoute;
